import { invariantResponse } from '@epic-web/invariant'
import { type LoaderFunctionArgs, type MetaFunction } from '@remix-run/node'
import {
	Form,
	Link,
	json,
	useFetcher,
	useLoaderData,
	useSearchParams,
} from '@remix-run/react'
import { useEffect, useState } from 'react'
import { CloudFrontImage } from '#app/components/cloudfront-image.js'
import { AspectRatio } from '#app/components/ui/aspect-ratio.js'
import { Button } from '#app/components/ui/button.js'
import { Dialog, DialogContent } from '#app/components/ui/dialog'
import { Icon } from '#app/components/ui/icon.js'
import Works from '#app/components/works.js'
import { getUserId } from '#app/utils/auth.server.js'
import { prisma } from '#app/utils/db.server.js'
import { useOptionalUser } from '#app/utils/user.js'
import {
	formatFacebookUrl,
	formatInstagramUrl,
	formatPortfolioUrl,
	formatTwitterUrl,
} from '../admin+/members+/$username'
import { type ContributorWorks } from './$username.works'

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	return [{ title: `Kogia | ${data?.name}` }]
}

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
	const userId = await getUserId(request)
	const works = new URL(request.url).searchParams.get('works') ?? 'all'
	const user = await prisma.user.findUnique({
		where: { username: params.username },
		select: {
			name: true,
			instagramUrl: true,
			facebookUrl: true,
			twitterUrl: true,
			biography: true,
			email: true,
			location: true,
			username: true,
			portfolioUrl: true,
			photo: {
				select: {
					work: {
						select: {
							id: true,
							originalAssetUrl: true,
							name: true,
						},
					},
				},
			},
			works: {
				select: {
					...(userId && {
						collections: {
							where: {
								isQuickCollection: true,
								userId: userId,
							},
							take: 1,
							select: { id: true },
						},
					}),
					user: {
						select: {
							username: true,
							name: true,
						},
					},
					id: true,
					name: true,
					originalAssetUrl: true,
					photo: {
						select: { id: true, width: true, height: true },
					},
					video: {
						select: {
							playbackId: true,
							maxHeight: true,
							maxWidth: true,
						},
					},
				},
				take: 24,
				...(works === 'photos' && {
					where: {
						photo: {
							isNot: null,
						},
					},
				}),
				...(works === 'videos' && {
					where: {
						video: {
							isNot: null,
						},
					},
				}),
				orderBy: {
					updatedAt: 'desc',
				},
			},
		},
	})
	invariantResponse(user, 'User not found', { status: 404 })
	return json(user)
}

export default function ArtistsIndex() {
	const optionalUser = useOptionalUser()
	const user = useLoaderData<typeof loader>()
	const [modalOpen, setModalOpen] = useState<boolean>(false)
	const [totalPages, setTotalPages] = useState<number>(Infinity)
	const [page, setPage] = useState<number>(2)
	const [works, setWorks] = useState(user.works)
	const fetcher = useFetcher<ContributorWorks>()
	const [searchParams] = useSearchParams()
	const worksQuery = searchParams.get('works') ?? 'all'
	const loadMore = () => {
		// if (optionalUser) {
		fetcher.load(
			`/contributors/${user.username}/works?page=${page}&works=${worksQuery}&userId=${optionalUser?.id ?? 'anonymous'}`,
		)
		setPage(p => p + 1)
		// }
	}
	useEffect(() => {
		setPage(2)
		setTotalPages(Infinity)
		setWorks(user.works)
	}, [user])
	useEffect(() => {
		if (!fetcher.data) return
		const [fetchedWorks, meta] = fetcher.data
		if (!fetchedWorks || !meta) return
		setTotalPages(meta.pageCount)
		setWorks(works => [...works, ...fetchedWorks])
		// eslint-disable-next-line
	}, [fetcher.data])
	// const workMarkup = works.map((work, index) => {
	// 	let loading: 'eager' | 'lazy' = index <= 6 ? 'eager' : 'lazy'
	// 	let workName =
	// 		work.name && work.name.trim() !== '' ? work.name : 'Untitled Work'
	// 	return (
	// 		<div
	// 			className="group"
	// 			key={`artists-show-${user.username}-work-${work.id}`}
	// 			onClick={() => {
	// 				if (!optionalUser) setModalOpen(true)
	// 			}}
	// 		>
	// 			<Link
	// 				className={`
	// 					relative mb-8 block outline-offset-8 outline-primary
	// 					${optionalUser ? 'pointer-events-auto' : 'pointer-events-none'}
	// 				`}
	// 				to={`/works/${work.id}`}
	// 			>
	// 				{work.photo && (
	// 					<CloudFrontImage
	// 						loading={loading}
	// 						work={{ ...work, user: { name: user.name } }}
	// 					/>
	// 				)}
	// 				{work.video?.playbackId && (
	// 					<div className="relative">
	// 						<img
	// 							loading={loading}
	// 							src={generateImageLink(work.video.playbackId)}
	// 							alt={workName}
	// 						/>
	// 						<img
	// 							loading={loading}
	// 							className="absolute left-0 top-0 h-full w-full opacity-0 group-hover:opacity-100 group-focus:opacity-100"
	// 							src={generateAnimatedImageLink(work.video.playbackId)}
	// 							alt={workName}
	// 						/>
	// 						<Icon
	// 							name="video"
	// 							className="absolute left-1/2 top-1/2 h-12 w-12 -translate-x-1/2 -translate-y-1/2 text-white"
	// 						/>
	// 					</div>
	// 				)}
	// 				<div className="mt-2 flex items-center gap-2 opacity-0 transition-opacity group-hover:opacity-100 group-focus:opacity-100">
	// 					<h3 className="font-serif text-xl font-bold">{workName}</h3>
	// 					<Separator className="flex-1" />
	// 				</div>
	// 			</Link>
	// 		</div>
	// 	)
	// })
	return (
		<>
			<Dialog
				open={modalOpen}
				onOpenChange={() => {
					setModalOpen(!open)
				}}
				defaultOpen={modalOpen}
			>
				<DialogContent className="flex max-h-[80vh] w-[calc(100vw-4rem)] max-w-screen-lg gap-0 overflow-hidden border-0 p-0 text-black md:h-[36rem] md:text-white">
					<div className="left flex flex-1 basis-1/3 flex-col gap-3 bg-white p-6 text-black md:p-8">
						<h2 className="font-serif text-h4">
							Join Kogia&apos;s
							<br />
							Oceanic Library
						</h2>
						<p>
							Apply for a free membership to access our library of photos and
							videos of the marine world, for you to use to communicate your
							work and tell your stories.
						</p>
						<Button
							asChild
							variant={'default'}
							className="mt-2 bg-black text-white"
						>
							<Link to="/apply">Apply</Link>
						</Button>
						<div className="flex flex-1 items-end text-center">
							<p className="w-full">
								Already have an account?{' '}
								<Link to="/login" className="text-black underline">
									Login
								</Link>
							</p>
						</div>
					</div>
					<div className="left hidden flex-1 basis-2/3 bg-secondary md:flex">
						<div className="flex flex-1 flex-col justify-end gap-3 bg-[url(/backgrounds/baitball-768.jpeg)] bg-cover p-8 pb-6 text-white sm:bg-[url(/backgrounds/baitball-1260.jpeg)] lg:bg-[url(/backgrounds/baitball-2160.jpeg)] xl:bg-[url(/backgrounds/baitball-original.jpg)]">
							<div className="flex w-3/4 flex-col gap-3">
								<h2 className="font-serif text-h4">
									A members-only library, built for conservation.
								</h2>
								<p>
									Our library is reserved for non-profit conservation purposes.
									It is intended for smaller conservation groups, researchers,
									scientists, activists, and artists who may not always have the
									resources to license media.
								</p>
								<p className="mb-0">
									We encourage larger organizations to sign up as Patrons in the
									application form. The monthly contributions from Patron
									members make it possible for Kogia to continue providing media
									for smaller organizations.
								</p>
							</div>
						</div>
					</div>
				</DialogContent>
			</Dialog>
			<div className="flex w-full flex-col-reverse md:min-h-[calc(100vh-4rem)] md:flex-row">
				<div className="flex flex-col justify-end border-r border-r-border bg-muted p-4 md:w-72 md:min-w-64 md:basis-1/4">
					{(user.portfolioUrl ||
						user.instagramUrl ||
						user.twitterUrl ||
						user.facebookUrl) && (
						<div className="mb-4 flex gap-4">
							{user.portfolioUrl && (
								<a
									href={formatPortfolioUrl(user.portfolioUrl)}
									target="_blank"
									title="Link to Portfolio"
									rel="noreferrer"
								>
									<Icon name="globe" className="text-h5" size="font">
										<span className="sr-only">Link to Portfolio</span>
									</Icon>
								</a>
							)}
							{user.instagramUrl && (
								<a
									href={formatInstagramUrl(user.instagramUrl)}
									target="_blank"
									title="Link to Instagram"
									rel="noreferrer"
								>
									<Icon name="instagram-logo" className="text-h5" size="font">
										<span className="sr-only">Link to Instagram</span>
									</Icon>
								</a>
							)}
							{user.twitterUrl && (
								<a
									href={formatTwitterUrl(user.twitterUrl)}
									target="_blank"
									title="Link to Twitter"
									rel="noreferrer"
								>
									<Icon name="twitter-logo" className="text-h5" size="font">
										<span className="sr-only">Link to Twitter</span>
									</Icon>
								</a>
							)}
							{user.facebookUrl && (
								<a
									href={formatFacebookUrl(user.facebookUrl)}
									target="_blank"
									title="Link to Facebook"
									rel="noreferrer"
									className="-ml-1"
								>
									<Icon name="facebook" className="text-h5" size="font">
										<span className="sr-only">Link to Facebook</span>
									</Icon>
								</a>
							)}
						</div>
					)}
					<div className="flex flex-col gap-2">
						<h2 className="font-serif text-4xl font-bold">{user.name}</h2>
						{user.location && <h3 className="text-xl">{user.location}</h3>}
						{user.biography && (
							<p
								className="text-muted-foreground"
								dangerouslySetInnerHTML={{
									__html: user.biography.replaceAll('\n', '<br/>'),
								}}
							/>
						)}
					</div>
				</div>
				<div className="flex flex-1 items-center justify-center p-4 md:basis-3/4 md:p-8">
					<div className="max-w-screen-[1800px] relative w-full md:w-11/12 md:py-10">
						{user.photo?.work?.originalAssetUrl && (
							<CloudFrontImage
								className="block w-full md:hidden"
								sizes="75vw"
								work={{
									name: user.photo?.work?.name,
									originalAssetUrl: user.photo?.work?.originalAssetUrl,
									id: user.photo?.work?.id,
									user: {
										name: user.name,
									},
								}}
							/>
						)}
						<div className="hidden w-full md:block">
							<AspectRatio ratio={16 / 10} className="h-full w-full">
								{user.photo?.work?.originalAssetUrl && (
									<CloudFrontImage
										className="w-full"
										sizes="75vw"
										work={{
											name: user.photo?.work?.name,
											originalAssetUrl: user.photo?.work?.originalAssetUrl,
											id: user.photo?.work?.id,
											user: {
												name: user.name,
											},
										}}
									/>
								)}
							</AspectRatio>
						</div>
						<a
							// eslint-disable-next-line
							href="#content"
							className="absolute -bottom-2 left-1/2 hidden -translate-x-1/2 -translate-y-1/2 md:block"
						>
							<Icon name="chevron-down" size="xl"></Icon>
						</a>
					</div>
				</div>
			</div>
			<div id="content" className="flex w-full flex-col">
				<Form
					preventScrollReset
					method="GET"
					className="flex w-full flex-col gap-4 border-y p-4 md:flex-row"
				>
					<Button
						size="lg"
						type="submit"
						variant="outline"
						name="works"
						className={worksQuery === 'all' ? 'bg-muted' : ''}
						value={'all'}
					>
						Videos and Photos
					</Button>
					<Button
						size="lg"
						type="submit"
						variant="outline"
						name="works"
						className={worksQuery === 'videos' ? 'bg-muted' : ''}
						value={'videos'}
					>
						Videos
					</Button>
					<Button
						size="lg"
						type="submit"
						variant="outline"
						name="works"
						className={worksQuery === 'photos' ? 'bg-muted' : ''}
						value={'photos'}
					>
						Photos
					</Button>
				</Form>
				<div className="min-h-screen p-4 md:p-8">
					{works.length >= 1 ? (
						<>
							<Works works={works} isLoggedIn={!!optionalUser} />
							{/* <Masonry
								breakpointCols={{
									default: 3,
									1280: 2,
									768: 1,
								}}
								className="my-masonry-grid"
								columnClassName="my-masonry-grid_column"
							>
								{workMarkup}
							</Masonry> */}
							{page <= totalPages && (
								<div className="flex justify-center">
									<Button
										disabled={fetcher.state !== 'idle'}
										onClick={loadMore}
										size={'lg'}
										className="text-h6 uppercase tracking-widest"
									>
										Load More
									</Button>
								</div>
							)}
						</>
					) : (
						<div>
							<h3 className="font-serif text-xl">No works to display</h3>
						</div>
					)}
				</div>
			</div>
		</>
	)
}
